import React, { useImperativeHandle, useRef, useState, forwardRef, useCallback, useMemo, useEffect } from 'react';
import BaseSearchBox from '../BaseSearchBox';
import useState2RM from '../../hooks/useState2ReduxMock';
import { Thunks } from '@web-common/state/reducer/mailReducer';
import { useActions, useAppDispatch, MailActions } from '@web-common/state/createStore';
import { Tooltip } from 'antd';
import { actions as mailTabActions } from '@web-common/state/reducer/mailTabReducer';
import InputContextMenu from '@web-common/components/UI/InputContextMenu/InputContextMenu';
import LxInput from '@lingxi-common-component/sirius-ui/Input';
import ProductAuthTag from '@web-common/components/UI/ProductAuthTag/ProductAuthTag';
import useMsgRenderCallback from '@web-common/hooks/useMsgRenderCallback';
import { TongyongGuanbiMian } from '@sirius/icons';
import { apiHolder as api, apis, ProductTagEnum, } from 'api';
import debounce from 'lodash/debounce';
import useCreateCallbackForEvent from '@web-common/hooks/useCreateCallbackForEvent';
import { getIn18Text } from 'api';
import { folderIdIsAllFolder } from '../../util';
const trackApi = api.api.requireLogicalApi(apis.dataTrackerApiImp);
const systemApi = api.api.getSystemApi();
const SearchBox = (props, ref) => {
    const { totalAccount, inputValue, setInputValue, advancedSearchForm } = props;
    const reducer = useActions(MailActions);
    const dispatch = useAppDispatch();
    const inputRef = useRef(null);
    const wrapRef = useRef(null);
    const [inputHover, setInputHover] = useState(false);
    const [searchBoxVisible, setSearchBoxVisible] = useState(false);
    const [mailSearchAccount] = useState2RM('mailSearchAccount', 'doUpdateMailSearchAccount');
    const [advancedSearchVisible, setAdvancedSearchVisible] = useState2RM('advancedSearchVisible', 'doUpdateAdvancedSearchVisible');
    const [, setSearchMail] = useState2RM('', 'doUpdateActiveSearchMailId');
    const [searchType, setSearchType] = useState2RM('mailSearchType', 'doUpdateMailSearchType');
    const [, setMailSearchRecord] = useState2RM('', 'doUpdateMailSearchRecord');
    const isComposition = useRef(false);
    const [mailSearchStateMap, doUpdateMailSearchStateMap] = useState2RM('mailSearchStateMap', 'doUpdateMailSearchStateMap');
    const [, setSearchLoading] = useState2RM('', 'doUpdateMailSearchLoading');
    const [, setListLoading] = useState2RM('', 'doUpdateMailListLoading');
    const [mailSearchKey, setMailSearchKey] = useState2RM('mailSearchKey', 'doUpdateMailSearchKey');
    const [mailSearching, doUpdateMailSearching] = useState2RM('mailSearching', 'doUpdateMailSearching');
    const [useRealList, _] = useState2RM('useRealList', 'doUpdateUseRealList');
    const isSearching = useMemo(() => !!mailSearching, [mailSearching]);
    const isAdvancedSearch = useMemo(() => mailSearching === 'advanced', [mailSearching]);
    const [selectedKeys] = useState2RM('selectedKeys', 'doUpdateSelectedKey');
    const [mailSearchMoreAccounts, setMailSearchMoreAccounts] = useState2RM('mailSearchMoreAccounts', 'doUpdateMailSearchMoreAccounts');
    const switchDefaultTab = useCallback(() => {
        dispatch(mailTabActions.doChangeCurrentTab('-1'));
    }, []);
    const onSearchTypeChange = useCallback((type, toRecord) => {
        setSearchMail({
            id: '',
            accountId: '',
        });
        setSearchType(type);
        setMailSearchRecord(toRecord);
    }, [setSearchMail, setSearchType, setMailSearchRecord]);
    const onSearchMail = useCallback(debounce((value, account) => {
        const sType = mailSearchStateMap[account] || (useRealList ? 'server' : 'local');
        if (!useRealList) {
            reducer.onSearchMailUpdateTreeStateMap({ account, value });
        }
        else {
            reducer.beforeSearchServer({ account, value, useRealList });
        }
        dispatch(Thunks.searchMail(Object.assign({ value }, (sType === 'local' ? { extraCloud: true } : null))));
        trackApi.track('pcMail_executeMailSearch', {
            searchMode: `全文搜索（${sType === 'local' ? '本地' : '云端'}）`,
        });
    }, 500), [totalAccount, mailSearchStateMap]);
    const onSearchMailRef = useCreateCallbackForEvent(onSearchMail);
    const onSearchChange = useCallback(debounce((value, account, showLoading = true) => {
        var _a;
        switchDefaultTab();
        if (!value) {
            reducer.reseOnSearchChange({});
            (advancedSearchForm === null || advancedSearchForm === void 0 ? void 0 : advancedSearchForm.resetFields) && advancedSearchForm.resetFields();
            reducer.doResetMailSearch({});
            setAdvancedSearchVisible(false);
            setMailSearchMoreAccounts(totalAccount && totalAccount.length > 1);
        }
        else {
            !account && setSearchLoading(showLoading);
            setListLoading(true);
            const userInfo = systemApi.getCurrentUser();
            const mainAccount = (userInfo === null || userInfo === void 0 ? void 0 : userInfo.id) || '';
            const defaultAccount = folderIdIsAllFolder(selectedKeys === null || selectedKeys === void 0 ? void 0 : selectedKeys.id) ? mainAccount : selectedKeys.accountId || ((_a = totalAccount[0]) === null || _a === void 0 ? void 0 : _a.value) || mainAccount;
            onSearchMailRef(value, account || mailSearchAccount || defaultAccount);
        }
    }, 500), [selectedKeys.accountId, selectedKeys.id, mailSearchAccount]);
    const onSearchInputChange = useCallback((inputValue) => {
        setSearchMail({
            id: '',
            accountId: '',
        });
        setInputValue(inputValue);
        if (!isComposition.current) {
            setSearchType('all');
            onSearchChange && onSearchChange(inputValue);
            trackApi.track('pcMail_input_searchContent_searchInputBox');
            if (inputValue) {
                setSearchBoxVisible(true);
            }
        }
    }, [onSearchChange]);
    const onSearchInputFocus = useCallback(() => {
        if (isAdvancedSearch && isSearching) {
            setSearchBoxVisible(false);
            setAdvancedSearchVisible(true);
        }
        else {
            setAdvancedSearchVisible(false);
            trackApi.track('pcMail_click_searchInputBox_topBar');
            setSearchBoxVisible(true);
        }
    }, [isSearching, isAdvancedSearch]);
    const onSearchInputBlur = useCallback((e) => {
        var _a, _b;
        if (!((_b = (_a = e === null || e === void 0 ? void 0 : e.relatedTarget) === null || _a === void 0 ? void 0 : _a.classList) === null || _b === void 0 ? void 0 : _b.contains('ant-dropdown-menu-item'))) {
            setSearchBoxVisible(false);
        }
    }, []);
    const onCompositionEnd = useCallback((e) => {
        isComposition.current = false;
        onSearchChange && onSearchChange(e.target.value);
        trackApi.track('pcMail_click_enter_keyboard');
    }, [onSearchChange]);
    const onCompositionStart = useCallback(() => {
        isComposition.current = true;
    }, []);
    const SearchInputSuffix = useMemo(() => {
        if (inputValue && (inputHover || isSearching)) {
            return (React.createElement(React.Fragment, null,
                React.createElement(TongyongGuanbiMian, { className: "u-mail-search-clear-icon", onClick: e => {
                        e.stopPropagation();
                        onSearchInputChange('');
                        setSearchBoxVisible(false);
                        if (inputRef.current) {
                            inputRef.current.blur();
                        }
                    } }),
                React.createElement(Tooltip, { title: getIn18Text('GAOJISOUSUO'), placement: "right" },
                    React.createElement(ProductAuthTag, { tagName: ProductTagEnum.ADVANCED_SEARCH, tipText: getIn18Text('XIANSHI') },
                        React.createElement("i", { className: "u-mail-search-advanced-icon", onClick: e => {
                                e.stopPropagation();
                                trackApi.track('pcMail_click_advancedSearch');
                                setAdvancedSearchVisible(true);
                            } })))));
        }
        else if (!!inputValue) {
            return (React.createElement(TongyongGuanbiMian, { className: "u-mail-search-clear-icon", onClick: e => {
                    e.stopPropagation();
                    onSearchInputChange('');
                    setSearchBoxVisible(false);
                    if (inputRef.current) {
                        inputRef.current.blur();
                    }
                } }));
        }
        else if (inputHover || isSearching) {
            return (React.createElement(Tooltip, { title: getIn18Text('GAOJISOUSUO'), placement: "right" },
                React.createElement(ProductAuthTag, { tagName: ProductTagEnum.ADVANCED_SEARCH, tipText: getIn18Text('XIANSHI') },
                    React.createElement("i", { className: "u-mail-search-advanced-icon", onClick: e => {
                            e.stopPropagation();
                            trackApi.track('pcMail_click_advancedSearch');
                            setAdvancedSearchVisible(true);
                        } }))));
        }
        return null;
    }, [inputHover, isSearching, advancedSearchVisible, inputValue]);
    const onPressEnter = useCallback((value) => {
        isComposition.current = false;
        setSearchBoxVisible(false);
        if (inputRef.current) {
            inputRef.current.blur();
        }
        if (value !== inputValue) {
            onSearchChange && onSearchChange(value);
            trackApi.track('pcMail_click_enter_keyboard');
        }
    }, [inputValue, onSearchChange]);
    useImperativeHandle(ref, () => wrapRef.current);
    useEffect(() => {
        if (advancedSearchVisible && inputRef.current) {
            inputRef.current.blur();
        }
    }, [advancedSearchVisible]);
    useMsgRenderCallback('mailMenuOper', ev => {
        if ((ev === null || ev === void 0 ? void 0 : ev.eventStrData) == 'searchInputFocus') {
            if (inputRef.current) {
                setTimeout(() => {
                    var _a;
                    (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, 200);
            }
        }
    });
    return (React.createElement("div", { ref: wrapRef, onMouseEnter: () => setInputHover(true), onMouseLeave: () => setInputHover(false), className: "m-search-container" },
        React.createElement(BaseSearchBox, { visible: searchBoxVisible, setVisible: setSearchBoxVisible, setAdvancedSearchVisible: setAdvancedSearchVisible, searchWord: inputValue || '', setSearchType: type => onSearchTypeChange(type, true), setInputValue: onSearchInputChange, onSearchChange: onSearchChange, searchType: searchType },
            React.createElement(InputContextMenu, { inputOutRef: inputRef, changeVal: setInputValue },
                React.createElement(LxInput, { ref: inputRef, placeholder: getIn18Text('SOUSUOYOUJIAN'), maxLength: 100, value: typeof inputValue === 'string' ? inputValue : mailSearchKey, style: { padding: 0, paddingLeft: 10 }, className: "sirius-no-drag", onFocus: onSearchInputFocus, onBlur: onSearchInputBlur, onCompositionStart: onCompositionStart, onCompositionEnd: onCompositionEnd, onPressEnter: e => onPressEnter(e.target.value), onChange: e => onSearchInputChange(e.target.value), prefix: React.createElement("i", { className: "dark-invert m-search-icon" }), suffix: SearchInputSuffix, allowClear: false })))));
};
export default forwardRef(SearchBox);
